<script setup lang="ts">
import { TESTIMONIALS, TESTIMONIAL_RATING_SCORE, type TestimonialMeta } from '~/constants/testimonials'

const CLICK_THRESHOLD_PX = 12
const SCROLL_SPEED_MS = 14000
const INTERACTION_SPEED_MS = 450
const SWIPER_LINEAR_SCROLL_STYLES = [
  ':host .swiper-wrapper { transition-timing-function: linear !important; }',
]

const props = withDefaults(defineProps<{
  items?: TestimonialMeta[]
  compact?: boolean
}>(), {
  items: () => TESTIMONIALS,
  compact: false,
})

const { t } = useI18n()

type SwiperAutoplay = {
  start: () => void
  stop: () => void
}

type SwiperInstance = {
  autoplay?: SwiperAutoplay
  params?: { speed?: number }
  on: (event: string, handler: (...args: unknown[]) => void) => void
}

type SwiperContainerEl = HTMLElement & {
  initialize?: () => void
  swiper?: SwiperInstance
}

const swiperRef = ref<SwiperContainerEl | null>(null)
const carouselRef = ref<HTMLElement | null>(null)
const modalRef = ref<HTMLElement | null>(null)
const overlayRef = ref<HTMLElement | null>(null)
const closeBtnRef = ref<HTMLButtonElement | null>(null)
const activeId = ref<string | null>(null)
const pointerStart = ref<{ x: number; y: number } | null>(null)
const prefersReducedMotion = ref(false)
const lastTriggerEl = ref<HTMLElement | null>(null)

const loopEnabled = computed(() => props.items.length >= 6)

function getSwiperOptions() {
  return {
    loop: loopEnabled.value,
    loopAdditionalSlides: props.items.length,
    grabCursor: true,
    injectStyles: SWIPER_LINEAR_SCROLL_STYLES,
    speed: SCROLL_SPEED_MS,
    slidesPerView: 'auto' as const,
    spaceBetween: 16,
    allowTouchMove: true,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
  }
}

const swiper = useSwiper(swiperRef, getSwiperOptions())

const activeItem = computed(() =>
  props.items.find(item => item.id === activeId.value) ?? null,
)

function getSwiperInstance() {
  return swiper.instance.value ?? swiperRef.value?.swiper
}

function capitalizeFirst(value: string) {
  if (!value) return value
  return value.charAt(0).toLocaleUpperCase() + value.slice(1)
}

function metaLine(item: TestimonialMeta) {
  const source = capitalizeFirst(t(`testimonials.sourceTypes.${item.source}`))
  return `${t(`testimonials.items.${item.id}.date`)} | ${source}`
}

function shouldBlockAutoplay() {
  return Boolean(activeId.value || prefersReducedMotion.value)
}

function pauseAutoplay() {
  getSwiperInstance()?.autoplay?.stop()
}

function resumeAutoplay() {
  if (shouldBlockAutoplay()) return
  getSwiperInstance()?.autoplay?.start()
}

let dragHandlersAttached = false
let dragMoved = false
let restoreMarqueeSpeedAfterTouch = false

function setSwiperSpeed(instance: SwiperInstance, speed: number) {
  if (instance.params) instance.params.speed = speed
}

function restoreMarqueeSpeed(instance: SwiperInstance) {
  if (!restoreMarqueeSpeedAfterTouch) return
  restoreMarqueeSpeedAfterTouch = false
  setSwiperSpeed(instance, SCROLL_SPEED_MS)
  resumeAutoplay()
}

function attachSwiperHandlers(instance: SwiperInstance) {
  if (dragHandlersAttached) return
  dragHandlersAttached = true

  instance.on('transitionEnd', () => {
    restoreMarqueeSpeed(instance)
  })

  instance.on('touchStart', () => {
    dragMoved = false
    setSwiperSpeed(instance, INTERACTION_SPEED_MS)
  })
  instance.on('sliderMove', () => {
    dragMoved = true
  })
  instance.on('touchEnd', () => {
    restoreMarqueeSpeedAfterTouch = true
    // Fallback if Swiper does not emit transitionEnd (already snapped).
    setTimeout(() => restoreMarqueeSpeed(instance), INTERACTION_SPEED_MS + 80)
  })
  resumeAutoplay()
}

function clearPointerStart() {
  pointerStart.value = null
}

function onPointerDown(event: PointerEvent) {
  pointerStart.value = { x: event.clientX, y: event.clientY }
}

function onSlideActivate(event: PointerEvent, id: string) {
  if (!pointerStart.value) return

  if (dragMoved) {
    clearPointerStart()
    return
  }

  const dx = Math.abs(event.clientX - pointerStart.value.x)
  const dy = Math.abs(event.clientY - pointerStart.value.y)
  clearPointerStart()

  if (dx >= CLICK_THRESHOLD_PX || dy >= CLICK_THRESHOLD_PX) return

  openModal(id)
}

function onCardKeydown(event: KeyboardEvent, id: string) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openModal(id, true)
  }
}

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )).filter(el => !el.hasAttribute('disabled'))
}

function onOverlayKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeModal()
    return
  }

  if (event.key !== 'Tab' || !modalRef.value) return

  const focusable = getFocusableElements(modalRef.value)
  if (focusable.length === 0) return

  const first = focusable[0]!
  const last = focusable[focusable.length - 1]!

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function openModal(id: string, focusClose = false) {
  lastTriggerEl.value = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
  clearPointerStart()
  activeId.value = id
  pauseAutoplay()
  document.body.style.overflow = 'hidden'
  nextTick(() => {
    if (focusClose) {
      closeBtnRef.value?.focus()
    } else {
      overlayRef.value?.focus({ preventScroll: true })
    }
  })
}

function closeModal() {
  activeId.value = null
  document.body.style.overflow = ''
  nextTick(() => {
    lastTriggerEl.value?.focus()
    lastTriggerEl.value = null
    resumeAutoplay()
  })
}

watch(() => swiper.instance.value, (instance) => {
  if (instance) nextTick(() => attachSwiperHandlers(instance))
}, { flush: 'post' })

onMounted(() => {
  prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (prefersReducedMotion.value) {
    pauseAutoplay()
  }

  if (import.meta.dev) {
    for (const item of props.items) {
      const quoteKey = `testimonials.items.${item.id}.quote`
      if (t(quoteKey) === quoteKey) {
        console.warn(`[TestimonialsCarousel] Missing i18n key: ${quoteKey}`)
      }
    }
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <section
    ref="carouselRef"
    class="testimonials-carousel"
    :class="{ compact }"
    :aria-label="t('testimonials.ariaLabel')"
    :inert="Boolean(activeId)"
  >
    <ClientOnly>
      <swiper-container ref="swiperRef" :init="false" class="testimonials-swiper">
        <swiper-slide v-for="item in items" :key="item.id">
          <div
            class="testimonial-card"
            role="button"
            tabindex="0"
            :aria-label="t('testimonials.openReview', { id: item.id })"
            @pointerdown="onPointerDown"
            @pointerup="onSlideActivate($event, item.id)"
            @pointercancel="clearPointerStart"
            @pointerleave="clearPointerStart"
            @keydown="onCardKeydown($event, item.id)"
          >
            <div class="testimonial-author">
              <span
                class="testimonial-avatar"
                :style="{ backgroundColor: item.avatarColor }"
                aria-hidden="true"
              >{{ item.initial }}</span>
              <span class="testimonial-rating">{{ TESTIMONIAL_RATING_SCORE }}</span>
            </div>
            <p class="testimonial-quote">
              {{ t(`testimonials.items.${item.id}.quote`) }}
            </p>
            <p class="testimonial-meta">{{ metaLine(item) }}</p>
          </div>
        </swiper-slide>
      </swiper-container>
    </ClientOnly>

    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="activeId && activeItem"
          ref="overlayRef"
          class="testimonial-overlay"
          tabindex="-1"
          @click.self="closeModal"
          @keydown="onOverlayKeydown"
        >
          <div
            ref="modalRef"
            class="testimonial-modal"
            role="dialog"
            aria-modal="true"
            :aria-label="t('testimonials.modalAriaLabel')"
          >
            <button
              ref="closeBtnRef"
              type="button"
              class="testimonial-close"
              :aria-label="t('testimonials.modalClose')"
              @click="closeModal"
            >
              <Icon name="tabler:x" size="1rem" />
            </button>
            <div class="testimonial-modal-header">
              <ul class="testimonial-modal-stars" aria-hidden="true">
                <li v-for="star in 5" :key="star">
                  <img src="/images/star.png" alt="" width="37" height="35" />
                </li>
              </ul>
              <div class="testimonial-modal-author">
                <span
                  class="testimonial-avatar"
                  :style="{ backgroundColor: activeItem.avatarColor }"
                  aria-hidden="true"
                >{{ activeItem.initial }}</span>
                <span class="testimonial-rating">{{ TESTIMONIAL_RATING_SCORE }}</span>
              </div>
            </div>
            <p class="testimonial-modal-quote">
              {{ t(`testimonials.items.${activeItem.id}.quote`) }}
            </p>
            <p class="testimonial-meta testimonial-modal-meta">{{ metaLine(activeItem) }}</p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<style scoped>
.testimonials-carousel {
  padding-top: 2rem;
  padding-bottom: 2rem;

  &.compact {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}

.testimonials-swiper {
  width: 100%;
  overflow: hidden;
}

.testimonials-swiper::part(wrapper) {
  transition-timing-function: linear !important;
}

.testimonials-swiper swiper-slide {
  width: auto;
  flex-shrink: 0;
  height: auto;
}

.testimonial-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 240px;
  height: 9.75rem;
  background-color: var(--white);
  border: 2px solid var(--ligth-blue);
  color: var(--blue);
  padding: 0.5rem 1.7rem;
  border-radius: 1.25rem;
  box-sizing: border-box;
  cursor: pointer;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  flex-shrink: 0;
  width: 100%;
}

.testimonial-avatar {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}

.testimonial-rating {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
}

.testimonial-quote {
  margin: 0;
  font-size: 12px;
  line-height: 22px;
  color: #000;
  text-align: left;
  width: 100%;
  height: 66px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  flex-shrink: 0;
}

.testimonial-meta {
  font-size: 0.8rem;
  font-weight: 400;
  white-space: nowrap;
}

.testimonial-card .testimonial-meta {
  margin-top: auto;
  margin-bottom: 0;
  flex-shrink: 0;
}

.testimonial-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 1rem;
  outline: none;
}

.testimonial-modal {
  background: var(--white);
  border: 1px solid var(--ligth-blue);
  border-radius: 15px;
  padding: 1.5rem;
  width: 100%;
  max-width: 22rem;
  min-height: 12rem;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  color: var(--blue);
}

.testimonial-modal-header {
  container-type: inline-size;
  position: relative;
  width: 100%;
  padding-top: 0.25rem;
  padding-right: 2rem;
  box-sizing: border-box;
  min-height: 1.75rem;
}

.testimonial-modal-stars {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
  flex-shrink: 0;

  li {
    display: flex;
  }

  img {
    width: 20px;
    height: 20px;
    display: block;
  }
}

.testimonial-modal-author {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

@container (max-width: 14.5rem) {
  .testimonial-modal-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    min-height: 0;
  }

  .testimonial-modal-stars {
    position: static;
    transform: none;
    align-self: center;
    order: -1;
  }
}

.testimonial-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--ligth-blue);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--dark-blue);
  transition: background-color 0.2s;
  padding: 0;
}

.testimonial-close:focus {
  outline: none;
}

.testimonial-close:focus-visible {
  outline: 2px solid var(--dark-blue);
  outline-offset: 2px;
}

.testimonial-close:hover {
  background: var(--light-gray);
}

.testimonial-modal-quote {
  margin: 0;
  padding-right: 0;
  font-size: 12px;
  line-height: 22px;
  color: #000;
  flex: 1;
  text-align: left;
}

.testimonial-modal-meta {
  margin-top: auto;
  margin-bottom: 0;
  width: 100%;
  text-align: center;
  padding-top: 1rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .testimonials-swiper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
